/**
 * Two-mode compiled HTML fragment validator for workspace apps.
 *
 * "strict" mode  - blocks on any violation (for render_spec sourced HTML).
 * "report_only"  - collects findings but never blocks (for legacy HTML).
 *
 * Design: intentionally pure (no Fastify, no request context) so it can be
 * used from both server-side route handlers and the client-side AppContainer
 * bundle. Callers must pass the sandbox-styles.css content as `cssString`.
 *
 * CSS allowlist source: callers supply sandbox-styles.css content (server:
 * read via node:fs; browser: Vite ?raw import). Parsed once per unique
 * string, then cached.
 */

import type { RestrictionExplanation } from "../../../../src/middleware/restriction-types.js";

// ─── Types ─────────────────────────────────────────────────────────────────

export interface Finding {
  rule: string;
  detail: string;
}

export type ValidationResult =
  | { kind: "ok" }
  | { kind: "blocked"; restriction: RestrictionExplanation }
  | { kind: "report_only"; findings: Finding[] };

// ─── Allowlists ──────────────────────────────────────────────────────────────

/** HTML tags allowed in compiled workspace fragments. */
export const ALLOWED_TAGS = new Set([
  "div",
  "span",
  "a",
  "button",
  "ul",
  "ol",
  "li",
  "table",
  "thead",
  "tbody",
  "tr",
  "td",
  "th",
  "p",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "strong",
  "em",
  "small",
  "form",
  "input",
  "label",
  "img",
]);

/**
 * HTML attributes allowed on compiled workspace fragments.
 * aria-* and data-agent-action-* are handled by prefix check in the walker.
 */
export const ALLOWED_ATTRS = new Set([
  "class",
  "href",
  "src",
  "alt",
  "role",
  "data-agent-action-type",
  "data-agent-action-payload",
  "data-testid",
  "for",
  "name",
  "type",
  "value",
  "placeholder",
  "style",
]);

// ─── CSS class allowlist ──────────────────────────────────────────────────────

/** Extract CSS class names from a stylesheet string. */
function parseCssClasses(css: string): Set<string> {
  const classes = new Set<string>();
  const re = /\.([a-zA-Z][a-zA-Z0-9_-]*)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(css)) !== null) {
    classes.add(m[1]);
  }
  return classes;
}

/** Per-string parse cache — avoids re-parsing on every call. */
const cssClassCache = new Map<string, Set<string>>();

export function getSandboxCssClasses(cssString: string): Set<string> {
  const hit = cssClassCache.get(cssString);
  if (hit) return hit;
  const result = parseCssClasses(cssString);
  cssClassCache.set(cssString, result);
  return result;
}

/** Test helper — clear the parse cache between tests. */
export function _resetCssClassCache(): void {
  cssClassCache.clear();
}

// ─── HTML Walker (regex-based) ────────────────────────────────────────────────

function walkHtml(html: string, cssClasses: Set<string>): Finding[] {
  const findings: Finding[] = [];

  // 1. Block <script> tags
  if (/<script[\s>]/i.test(html)) {
    findings.push({ rule: "no_script", detail: "Contains <script> element" });
  }

  // 2. Block javascript: URIs
  if (/javascript:/i.test(html)) {
    findings.push({ rule: "no_javascript_href", detail: "Contains javascript: URI" });
  }

  // 3. Block on* event handler attributes (onclick=, onerror=, etc.)
  const onAttrMatches = html.match(/\s+on[a-zA-Z]+\s*=/gi);
  if (onAttrMatches) {
    const unique = [
      ...new Set(onAttrMatches.map((m) => m.trim().split(/\s*=/)[0].toLowerCase())),
    ];
    findings.push({
      rule: "no_inline_event_handlers",
      detail: `Contains inline event handler(s): ${unique.join(", ")}`,
    });
  }

  // 4. Walk opening tags for unknown tags / unknown attrs / unknown classes
  const tagRe = /<([a-zA-Z][a-zA-Z0-9-]*)(\s[^>]*)?\s*\/?>/g;
  let tagMatch: RegExpExecArray | null;
  while ((tagMatch = tagRe.exec(html)) !== null) {
    const tagName = tagMatch[1].toLowerCase();
    const attrsStr = tagMatch[2] ?? "";

    if (!ALLOWED_TAGS.has(tagName)) {
      findings.push({ rule: "unknown_tag", detail: `Unknown tag: <${tagName}>` });
    }

    const attrRe = /\s+([a-zA-Z][a-zA-Z0-9_:-]*)(?:\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]*))?/g;
    let attrMatch: RegExpExecArray | null;
    while ((attrMatch = attrRe.exec(attrsStr)) !== null) {
      const attrName = attrMatch[1].toLowerCase();

      // Allowed by prefix
      if (attrName.startsWith("aria-") || attrName.startsWith("data-agent-action-")) continue;

      if (!ALLOWED_ATTRS.has(attrName)) {
        findings.push({ rule: "unknown_attr", detail: `Unknown attribute: ${attrName}` });
      }

      if (attrName === "class") {
        const valueMatch = attrMatch[0].match(/=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]*))/);
        const classValue = valueMatch
          ? (valueMatch[1] ?? valueMatch[2] ?? valueMatch[3] ?? "")
          : "";
        for (const cls of classValue.split(/\s+/).filter(Boolean)) {
          if (!cssClasses.has(cls)) {
            findings.push({ rule: "unknown_css_class", detail: `Unknown CSS class: ${cls}` });
          }
        }
      }
    }
  }

  return findings;
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Validate compiled workspace HTML.
 *
 * @param html      - The HTML fragment to validate.
 * @param mode      - "strict": block on first violation. "report_only": collect all, never block.
 * @param cssString - sandbox-styles.css content for the CSS class allowlist.
 */
export function validateCompiledWorkspaceHtml(
  html: string,
  mode: "strict" | "report_only",
  cssString: string,
): ValidationResult {
  const cssClasses = getSandboxCssClasses(cssString);
  const findings = walkHtml(html, cssClasses);

  if (mode === "report_only") {
    return { kind: "report_only", findings };
  }

  // Strict mode
  if (findings.length === 0) {
    return { kind: "ok" };
  }

  const first = findings[0];
  const restriction: RestrictionExplanation = {
    middleware: "workspace-html-validator",
    category: "content_policy",
    severity: "hard_block",
    title: "Compiled HTML failed security validation",
    evidence: first.detail,
    redactedDetail: findings.map((f) => `[${f.rule}] ${f.detail}`).join("; "),
    guidance: "Remove disallowed elements, attributes, or CSS classes from the HTML fragment.",
    resolutionOptions: ["retry_modified"],
    timestamp: Date.now(),
    ruleId: first.rule,
  };

  return { kind: "blocked", restriction };
}
